import { IndicationSurveyList, SurveyCategory } from '@abiglobalhealth/go-types'
import { useTranslation } from 'react-i18next'
import type { ActionMeta, ControlProps, SingleValue } from 'react-select'
import ReactSelect, { components } from 'react-select'

import { Search } from '@/components/icons/Search'
import { useSurveyCategories } from '@/hooks/useSurveyCategories'
import i18n from '@/locale/i18n'

interface Option {
  value: {
    category: Omit<SurveyCategory, 'options'>
  } & IndicationSurveyList

  label: string
}

const formatOptions = (categories: SurveyCategory[]): Option[] =>
  categories
    .map((category) =>
      category.options.map((option) => ({
        value: { category: { id: category.id, textKey: category.textKey, iconUrl: category.iconUrl }, ...option },
        label: i18n.t(option.textKey)
      }))
    )
    .flat()
    .sort((a, z) => a.label.localeCompare(z.label))

const Control = ({ children, ...props }: ControlProps<Option>) => (
  <components.Control
    className="border-gray-text focus-visible:border-primary hover:border-primary! flex h-16 flex-row items-center gap-2 rounded-lg! border bg-white px-4 shadow-none! focus-visible:outline-none"
    {...props}
  >
    <Search customClassName={props.isFocused ? 'text-primary' : 'text-gray-text'} />
    {children}
  </components.Control>
)

interface AutocompletedSearchProps {
  onChange: (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => void
}

export const AutocompletedSearch = ({ onChange }: AutocompletedSearchProps) => {
  const { t } = useTranslation()
  const { surveyCategories } = useSurveyCategories()

  return (
    <ReactSelect
      options={formatOptions(surveyCategories!)}
      className="w-full"
      placeholder="Search for a condition or medicine"
      isClearable
      onChange={onChange}
      isMulti={false}
      styles={{
        indicatorSeparator: () => ({
          display: 'none'
        }),
        dropdownIndicator: () => ({
          display: 'none'
        })
      }}
      formatOptionLabel={({ value, label }) => (
        <div className="flex flex-col gap-1 py-1">
          <span className="font-bold">{label}</span>
          <span className="text-gray-text pl-2 text-sm">{t(value.category.textKey)}</span>
        </div>
      )}
      components={{
        Control
      }}
    />
  )
}
